// What a page shows when it has nothing to show yet: the first full pull (FirstSyncProgress / RequireFirstSync), a
// generic loading layout (ScreenLoading), a failure (ScreenError), and the two empty states of store lists with the
// iOS wording (RecordListView: "No records yet" → "No stores yet", "No matches").
//
// The rule they encode (sync/README.md): before `initialFetchDone` an empty list means "still loading", never "no
// stores yet", so a page shows FirstSyncProgress first and only then its own empty state.

import { CloudDownload, RefreshCw, SearchX, Store, TriangleAlert, WifiOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useOptionalSyncEngine } from '@/sync/hooks';
import { useSyncStatus, type SyncStatus } from '@/sync/status';
import { Button, EmptyState, IconBadge, ProgressBar, Skeleton, SkeletonText, cardClass, cn } from '@/ui';
import { firstSyncCopy, firstSyncState, type FirstSyncState } from './firstSync';

// MARK: - First sync

/** The first-pull state of the running engine (`ready` once a pull has completed for this company). */
export function useFirstSync(): FirstSyncState & { ready: boolean } {
  const status = useSyncStatus();
  const state = firstSyncState(status);
  return { ...state, ready: state.kind === 'ready' };
}

export interface FirstSyncProgressProps {
  /** Overrides the live status (stories, tests). */
  status?: Pick<SyncStatus, 'initialFetchDone' | 'phase' | 'progress' | 'error'>;
  /** What the page will show, for the screen-reader label ("Loading stores"). */
  what?: string;
  className?: string;
}

/**
 * The first-sync card: an indeterminate bar with "N items so far" while the replica downloads, or the offline / error
 * message with Retry. Renders nothing once the first pull is done.
 */
export function FirstSyncProgress({ status, what, className }: FirstSyncProgressProps) {
  const live = useSyncStatus();
  const engine = useOptionalSyncEngine();
  const [retrying, setRetrying] = useState(false);
  const state = firstSyncState(status ?? live);
  if (state.kind === 'ready') return null;
  const copy = firstSyncCopy(state);
  const problem = state.kind !== 'loading';

  const retry = async () => {
    if (!engine) return;
    setRetrying(true);
    try {
      await engine.pullNow();
    } catch {
      // The status (offline / error) already says what went wrong.
    } finally {
      setRetrying(false);
    }
  };

  const Icon = state.kind === 'offline' ? WifiOff : state.kind === 'error' ? TriangleAlert : CloudDownload;
  const color = state.kind === 'error' ? '#DB4038' : state.kind === 'offline' ? '#8C919E' : '#2673E6';

  return (
    <div className={cn('flex w-full justify-center px-4 py-10 sm:py-16', className)}>
      <section
        aria-live="polite"
        aria-busy={!problem || undefined}
        aria-label={what ? `${what}: ${copy.title}` : copy.title}
        className={cn(cardClass, 'flex w-full max-w-md flex-col items-center gap-4 p-6 text-center sm:p-8')}
      >
        <IconBadge icon={<Icon />} color={color} size={52} className="shadow-[0_6px_12px_rgb(16_42_94/0.12)]" />
        <div className="flex flex-col gap-1.5">
          <h2 className="text-lg font-semibold text-fg">{copy.title}</h2>
          <p className="text-sm text-fg-muted">{copy.message}</p>
        </div>
        {!problem && (
          <ProgressBar
            label={what ? `Loading ${what.toLowerCase()}` : 'Loading your company data'}
            detail={copy.detail ?? undefined}
            showLabel={copy.detail !== null}
            tone="info"
            size="sm"
            className="w-full max-w-xs"
          />
        )}
        {problem && (
          <>
            {copy.detail && <p className="text-xs text-fg-muted tabular-nums">{copy.detail}</p>}
            <Button variant="secondary" icon={<RefreshCw />} loading={retrying} onClick={retry} disabled={!engine}>
              Retry now
            </Button>
          </>
        )}
      </section>
    </div>
  );
}

export interface RequireFirstSyncProps {
  /** The page; rendered once the first pull is done. */
  children: ReactNode;
  /** Screen-reader name of what is loading ("Stores"). */
  what?: string;
  /** Replaces the default FirstSyncProgress card (e.g. a skeleton of the page). */
  fallback?: ReactNode;
}

/** Renders `children` only after the first full pull; FirstSyncProgress (or `fallback`) until then. */
export function RequireFirstSync({ children, what, fallback }: RequireFirstSyncProps) {
  const sync = useFirstSync();
  if (sync.ready) return <>{children}</>;
  // A custom fallback only covers plain loading; offline and errors always get the card with Retry.
  if (fallback !== undefined && sync.kind === 'loading') return <>{fallback}</>;
  return <FirstSyncProgress what={what} />;
}

// MARK: - Loading and errors

export interface ScreenLoadingProps {
  /** Screen-reader text. */
  label?: string;
  /** `list`: stacked row skeletons; `cards`: a tile grid; `table`: header + rows; `center`: just a spinner line. */
  layout?: 'list' | 'cards' | 'table' | 'center';
  /** Number of skeleton rows / tiles. */
  count?: number;
  className?: string;
}

/** A skeleton of the page while something local is being prepared (the replica is ready; this is not first sync). */
export function ScreenLoading({ label = 'Loading…', layout = 'list', count = 6, className }: ScreenLoadingProps) {
  const items = Array.from({ length: Math.max(1, count) }, (_, i) => i);
  return (
    <div role="status" aria-busy="true" className={cn('w-full', className)}>
      <span className="sr-only">{label}</span>
      {layout === 'center' && (
        <div className="flex items-center justify-center py-16">
          <Skeleton className="h-4" width={160} />
        </div>
      )}
      {layout === 'list' && (
        <div className="flex flex-col gap-3">
          {items.map((i) => (
            <div key={i} className={cn(cardClass, 'flex items-start gap-3 p-4')}>
              <Skeleton className="size-10 shrink-0 rounded-[13px]" />
              <SkeletonText lines={3} className="flex-1" />
              <Skeleton className="h-[22px] w-8 rounded-full" />
            </div>
          ))}
        </div>
      )}
      {layout === 'cards' && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
          {items.map((i) => (
            <div key={i} className={cn(cardClass, 'flex flex-col gap-2 p-3')}>
              <Skeleton className="size-[30px] rounded-[10px]" />
              <Skeleton className="h-7" width="55%" />
              <Skeleton className="h-3" width="40%" />
            </div>
          ))}
        </div>
      )}
      {layout === 'table' && (
        <div className={cn(cardClass, 'overflow-hidden')}>
          <div className="flex gap-4 border-b border-line bg-surface-2 px-4 py-3">
            {[30, 14, 18, 12, 16].map((w, i) => (
              <Skeleton key={i} className="h-3" width={`${w}%`} />
            ))}
          </div>
          {items.map((i) => (
            <div key={i} className="flex items-center gap-4 border-b border-line px-4 py-3 last:border-b-0">
              <Skeleton className="h-3.5" width="30%" />
              <Skeleton className="h-3.5" width="14%" />
              <Skeleton className="h-3.5" width="18%" />
              <Skeleton className="h-3.5" width="12%" />
              <Skeleton className="h-3.5" width="16%" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export interface ScreenErrorProps {
  title?: string;
  /** Explanation; defaults to the error's message. */
  message?: ReactNode;
  error?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  /** Extra actions (e.g. a link back to the list). */
  action?: ReactNode;
  /** Inside a card or panel rather than as the whole page. */
  compact?: boolean;
  className?: string;
}

function errorText(error: unknown): string | null {
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  if (typeof error === 'string' && error.trim() !== '') return error;
  return null;
}

/** A failure the page cannot recover from by itself (a server query failed, an item is missing). */
export function ScreenError({
  title = 'Something went wrong',
  message,
  error,
  onRetry,
  retryLabel = 'Try again',
  action,
  compact = false,
  className,
}: ScreenErrorProps) {
  const text = message ?? errorText(error) ?? 'Please try again. If it keeps happening, refresh the page.';
  return (
    <div role="alert" className={cn('w-full', !compact && 'py-6', className)}>
      <EmptyState
        compact={compact}
        icon={<TriangleAlert />}
        color="#DB4038"
        title={title}
        message={text}
        action={
          onRetry || action ? (
            <>
              {onRetry && (
                <Button variant="secondary" icon={<RefreshCw />} onClick={onRetry}>
                  {retryLabel}
                </Button>
              )}
              {action}
            </>
          ) : undefined
        }
      />
    </div>
  );
}

// MARK: - Empty store lists

/** The company has no stores the viewer can see (only after the first sync). */
export function NoStoresYet({ message, action, compact, className }: { message?: ReactNode; action?: ReactNode; compact?: boolean; className?: string }) {
  return (
    <EmptyState
      compact={compact}
      icon={<Store />}
      title="No stores yet"
      message={message ?? 'Stores your team adds in the Ground Team app will show up here.'}
      action={action}
      className={className}
    />
  );
}

/** Filters or search hide every store (iOS "No matches"), with a Reset filters button when given `onReset`. */
export function NoMatches({ onReset, message, compact, className }: { onReset?: () => void; message?: ReactNode; compact?: boolean; className?: string }) {
  return (
    <EmptyState
      compact={compact}
      icon={<SearchX />}
      title="No matches"
      message={message ?? 'Adjust your search or reset the filters.'}
      action={
        onReset ? (
          <Button variant="secondary" onClick={onReset}>
            Reset filters
          </Button>
        ) : undefined
      }
      className={className}
    />
  );
}
